import { useCallback, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { parseApiError } from "utils/parseApiError";
import { HTTP_STATUS_CONFLICT } from "../../constants";
import type { FloorPlanFolderRecord } from "../../utils/types";
import type {
  FolderActionState,
  RenameFolderVariables,
  UseFolderActionsArgs,
  UseFolderActionsResult,
} from "./types";

export const useFolderActions = ({
  folderRecords,
  onFolderRenamed,
  onFolderDeleted,
}: UseFolderActionsArgs): UseFolderActionsResult => {
  const { t } = useTranslation();

  const [folderActionState, setFolderActionState] = useState<FolderActionState>(
    { phase: "idle" },
  );

  const renameFolderMutation = useMutation({
    mutationFn: ({
      folderId,
      folderName,
    }: RenameFolderVariables): Promise<FloorPlanFolderRecord> =>
      api.renameQuantitySurveyorFloorPlanFolder(folderId, { name: folderName }),
    onSuccess: (folder, { folderId }) => {
      onFolderRenamed?.(folderId, folder.name);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_FOLDER_RENAMED);
      // Only closes the modal that started this rename, not one opened meanwhile.
      setFolderActionState((current) =>
        current.phase === "renaming" && current.folderId === folderId
          ? { phase: "idle" }
          : current,
      );
    },
    onError: (error) => {
      const isNameTaken =
        parseApiError({ error }).status === HTTP_STATUS_CONFLICT;
      toast.error(
        t(
          isNameTaken
            ? "smartSurveyor.folders.nameTaken"
            : "smartSurveyor.folders.renameFailed",
        ),
      );
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: number): Promise<void> =>
      api.deleteQuantitySurveyorFloorPlanFolder(folderId),
    onSuccess: (_data, folderId) => {
      onFolderDeleted?.(folderId);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_FOLDER_DELETED);
    },
    onError: () => {
      toast.error(t("smartSurveyor.folders.deleteFailed"));
    },
  });

  const { mutate: renameFolder } = renameFolderMutation;
  const { mutateAsync: deleteFolder } = deleteFolderMutation;

  const openRenameFolder = useCallback((folderId: number) => {
    setFolderActionState({ phase: "renaming", folderId });
  }, []);

  const openDeleteFolder = useCallback((folderId: number) => {
    setFolderActionState({ phase: "deleting", folderId });
  }, []);

  const closeFolderAction = useCallback(() => {
    setFolderActionState({ phase: "idle" });
  }, []);

  const submitRenameFolder = useCallback(
    (folderName: string) => {
      if (folderActionState.phase !== "renaming") return;
      renameFolder({ folderId: folderActionState.folderId, folderName });
    },
    [folderActionState, renameFolder],
  );

  const confirmDeleteFolder = useCallback(async () => {
    if (folderActionState.phase !== "deleting") return;
    try {
      await deleteFolder(folderActionState.folderId);
    } catch {
      // Already reported by onError; the confirmation only needs to know it is done.
    }
  }, [folderActionState, deleteFolder]);

  // Only one folder modal is open at a time, so one lookup serves both.
  const activeFolder = useMemo(
    () =>
      folderActionState.phase === "idle"
        ? null
        : (folderRecords.find(
            (folder) => folder.id === folderActionState.folderId,
          ) ?? null),
    [folderActionState, folderRecords],
  );

  const renamingFolder =
    folderActionState.phase === "renaming" ? activeFolder : null;
  const deletingFolder =
    folderActionState.phase === "deleting" ? activeFolder : null;

  return {
    renamingFolder,
    isRenameSaving: renameFolderMutation.isPending,
    deletingFolder,
    openRenameFolder,
    closeRenameFolder: closeFolderAction,
    submitRenameFolder,
    openDeleteFolder,
    closeDeleteFolder: closeFolderAction,
    confirmDeleteFolder,
  };
};
